import React, { useState } from 'react';
import { useAssessment } from '../../context/AssessmentContext';
import ProgressPath from '../ui/ProgressPath';
import ParticleEffect from '../ui/ParticleEffect';
import AnimatedBackground from '../ui/AnimatedBackground';
import { UsersIcon, UserIcon, HeartIcon, MessageCircleIcon, BookIcon, Flower2Icon, LeafIcon } from 'lucide-react';
const CommunityPreferences: React.FC = () => {
  const {
    currentStep,
    setCurrentStep,
    assessmentData,
    updateAssessmentData
  } = useAssessment();
  const [selectedOption, setSelectedOption] = useState<string>(assessmentData.communityType || '');
  const communityOptions = [{
    id: 'solo-journey',
    title: 'Solo Journey',
    description: 'I prefer to explore my healing path privately and at my own pace.',
    icon: UserIcon,
    color: 'border-blue-400',
    gradient: 'from-blue-500/30 to-indigo-500/30'
  }, {
    id: 'guided-support',
    title: 'Guided Support',
    description: 'I value personalized guidance from coaches or healers along my journey.',
    icon: HeartIcon,
    color: 'border-purple-400',
    gradient: 'from-purple-500/30 to-pink-500/30'
  }, {
    id: 'community-connection',
    title: 'Community Connection',
    description: 'I thrive with supportive community and shared wisdom from fellow journeyers.',
    icon: UsersIcon,
    color: 'border-green-400',
    gradient: 'from-green-500/30 to-teal-500/30'
  }, {
    id: 'learning-focused',
    title: 'Learning Focused',
    description: 'I want to understand the science and wisdom behind healing practices.',
    icon: BookIcon,
    color: 'border-amber-400',
    gradient: 'from-amber-500/30 to-yellow-500/30'
  }];
  const handleContinue = () => {
    updateAssessmentData({
      communityType: selectedOption
    });
    setCurrentStep(currentStep + 1);
  };
  return <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative" data-id="element-545">
      <AnimatedBackground type="cosmic" data-id="element-546" />
      <ParticleEffect count={50} data-id="element-547" />
      {/* Decorative elements */}
      <div className="absolute top-10 left-10 garden-leaf opacity-20" data-id="element-548">
        <LeafIcon size={80} className="text-green-200" data-id="element-549" />
      </div>
      <div className="absolute bottom-20 right-20 garden-leaf opacity-20" data-id="element-550">
        <LeafIcon size={100} className="text-green-200" data-id="element-551" />
      </div>
      <div className="absolute top-1/4 right-1/4 garden-flower opacity-20" data-id="element-552">
        <Flower2Icon size={60} className="text-pink-200" data-id="element-553" />
      </div>
      <div className="w-full max-w-4xl" data-id="element-554">
        <h1 className="text-4xl md:text-5xl font-bold text-center text-white mb-2" data-id="element-555">
          <span className="text-pink-300" data-id="element-556">Seven</span>After
        </h1>
        <ProgressPath totalSteps={7} currentStep={5} data-id="element-557" />
        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white border-opacity-20 shadow-xl relative overflow-hidden" data-id="element-558">
          <div className="spiral-pattern absolute inset-0 opacity-20 z-0" data-id="element-559"></div>
          <div className="lotus-pattern absolute inset-0 opacity-20 z-0" data-id="element-560"></div>
          <h2 className="text-2xl md:text-3xl font-bold text-center text-white mb-4 relative z-10" data-id="element-561">
            How Would You Like to Be Supported?
          </h2>
          <p className="text-white text-center mb-8 relative z-10" data-id="element-562">
            Choose the type of support that feels most aligned with your healing
            journey right now.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 relative z-10" data-id="element-563">
            {communityOptions.map(option => {
            const isSelected = selectedOption === option.id;
            const Icon = option.icon;
            return <button key={option.id} onClick={() => setSelectedOption(option.id)} className={`
                    p-5 rounded-xl text-left transition-all border backdrop-blur-sm
                    ${isSelected ? `bg-gradient-to-br ${option.gradient} ${option.color} border-opacity-70 shadow-lg` : 'bg-white bg-opacity-5 border-white border-opacity-20 hover:bg-opacity-10'}
                  `} data-id="element-564">
                  <div className="flex items-center mb-3" data-id="element-565">
                    <div className={`
                      w-12 h-12 rounded-full flex items-center justify-center
                      bg-gradient-to-br ${option.gradient} relative
                    `} data-id="element-566">
                      <Icon size={24} className="text-white" data-id="element-567" />
                    </div>
                    <h3 className="text-xl font-bold text-white ml-3" data-id="element-568">
                      {option.title}
                    </h3>
                  </div>
                  <p className="text-white text-opacity-80" data-id="element-569">
                    {option.description}
                  </p>
                </button>;
          })}
          </div>
          <button onClick={handleContinue} disabled={!selectedOption} className={`
              w-full py-4 rounded-xl transition-all duration-300 text-white font-semibold relative z-10
              ${selectedOption ? 'mystical-button glow bg-gradient-to-r from-pink-500 to-orange-500 hover:shadow-lg cursor-pointer' : 'bg-gray-500 bg-opacity-50 cursor-not-allowed'}
            `} data-id="element-570">
            Continue Your Sacred Journey
          </button>
          {!selectedOption && <p className="text-center text-white text-opacity-60 mt-3 text-sm relative z-10" data-id="element-571">
              Please select a support preference to continue
            </p>}
        </div>
      </div>
    </div>;
};
export default CommunityPreferences;